/**
 * Measured CSS font metrics, so a replica can place type by BASELINE.
 *
 * ---------------------------------------------------------------------------
 * Why this exists
 * ---------------------------------------------------------------------------
 * A PDF places every run by its baseline. CSS has no way to say "put the
 * baseline here" — `top` positions the top of a LINE BOX, and the distance from
 * there down to the baseline depends on three things:
 *
 *     baselineOffset = halfLeading + ascent
 *                    = (lineHeight - contentBox) / 2  +  ascent
 *
 * `contentBox` and `ascent` are the font's own metrics as the BROWSER resolves
 * them, in em. They are not knowable from the font file alone, because engines
 * disagree about which table to read: Blink on macOS takes hhea
 * ascender/descender via Core Text, Blink on Windows may take OS/2
 * usWinAscent/usWinDescent or the typo metrics when USE_TYPO_METRICS is set.
 * For HelveticaNeueLTStd those two readings differ by 0.166em — at 8pt that is
 * 1.3pt of vertical drift, i.e. text visibly riding up into the rule above.
 *
 * The replicas used to absorb this with a single hand-tuned `line-height` per
 * form (the W-7 had 1.24, the SS-4 1.09, the 2848 1.15). That cannot work: the
 * error is proportional to font size, so one factor cannot be right for 7, 8,
 * 9, 10, 14 and 24pt on the same sheet. It left the 8pt body 1.2pt high and the
 * 14pt "Sign Here" 2.7pt high.
 *
 * So: measure it, once, in the browser that is actually going to draw the page.
 * Then `top` is arithmetic, not a fudge, and it is correct on every platform.
 *
 * ---------------------------------------------------------------------------
 * How the probe works
 * ---------------------------------------------------------------------------
 * contentBox — the rect of a single-line inline, non-replaced element is its
 *   content area, which is exactly ascent + descent. line-height does not
 *   affect it.
 *
 * ascent — a zero-size inline-block with `vertical-align: baseline` sits with
 *   its bottom margin edge ON the baseline, and contributes no height of its
 *   own. So its offset from the top of a block whose line-height is known gives
 *   the baseline offset directly, and `ascent` falls out of the identity above.
 *
 * Both are measured at PROBE_PX = 1000 and divided down, so integer pixel
 * snapping inside the layout engine costs at most 0.0005em — 0.004pt at 8pt.
 */

/** Font-size the probe runs at. Large, so rounding is negligible once divided. */
const PROBE_PX = 1000;

/** A face, as CSS resolves it. */
export type FaceSpec = {
  family: string;
  weight?: number;
  style?: "normal" | "italic";
};

/**
 * A face's vertical metrics, in em.
 *
 * `content` is the content-area height (ascent + descent); `ascent` is the
 * distance from the content-area top down to the baseline.
 */
export type FaceMetrics = { content: number; ascent: number };

/**
 * Distance from the TOP of a line box down to the baseline, in points.
 *
 * This is the whole point of the module: given a measured face, a type size and
 * a line pitch, it says where the baseline will land — so a caller that knows
 * where the baseline must GO can subtract and get `top`.
 */
export const baselineOffset = (
  m: FaceMetrics,
  sizePt: number,
  leadingPt: number,
): number => (leadingPt - sizePt * m.content) / 2 + sizePt * m.ascent;

const key = (f: FaceSpec) =>
  `${f.family}|${f.weight ?? 400}|${f.style ?? "normal"}`;

const measured = new Map<string, FaceMetrics>();

/**
 * Theoretical metrics from each font's hhea table, used until the probe has
 * run so first paint is already close instead of visibly wrong.
 *
 * Every HelveticaNeueLTStd cut carries hhea 0.714 / -0.286 / gap 0.200, and
 * ITCFranklinGothicStd-Demi carries 0.686 / -0.314 / 0.200 — so `content` is
 * 1.000em for both and only the ascent differs. These are what Blink resolves
 * on macOS; the probe corrects them anywhere that is not true.
 */
const SEED: Record<string, FaceMetrics> = {
  "ITCFranklinGothicStd-Demi": { content: 1.0, ascent: 0.686 },
};
const SEED_DEFAULT: FaceMetrics = { content: 1.0, ascent: 0.714 };

const seedFor = (f: FaceSpec): FaceMetrics => {
  for (const family of Object.keys(SEED)) {
    if (f.family.includes(family)) return SEED[family];
  }
  return SEED_DEFAULT;
};

/** Metrics for a face: measured if the probe has run, seeded otherwise. */
export const faceMetrics = (f: FaceSpec): FaceMetrics =>
  measured.get(key(f)) ?? seedFor(f);

/** True once `loadFormFontMetrics` has measured this face. */
export const isMeasured = (f: FaceSpec): boolean => measured.has(key(f));

const cssFont = (f: FaceSpec, px: number) =>
  `${f.style ?? "normal"} ${f.weight ?? 400} ${px}px ${f.family}`;

/**
 * Measures one face synchronously. The caller is responsible for having the
 * webfont loaded first — probing against a fallback would bake the fallback's
 * metrics in, which is the exact failure this module exists to prevent.
 */
const probe = (f: FaceSpec): FaceMetrics => {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:absolute;left:-99999px;top:0;margin:0;padding:0;border:0;" +
    "white-space:nowrap;visibility:hidden;";

  const font =
    `font-family:${f.family};font-weight:${f.weight ?? 400};` +
    `font-style:${f.style ?? "normal"};font-size:${PROBE_PX}px;`;

  // contentBox: an inline box's rect IS its content area.
  const inline = document.createElement("span");
  inline.style.cssText = `${font}line-height:normal;`;
  inline.textContent = "Hxp";

  // ascent: line-height == font-size, so halfLeading is (1 - content)/2 and the
  // zero-height strut's top lands on the baseline.
  const block = document.createElement("div");
  block.style.cssText = `${font}line-height:${PROBE_PX}px;`;
  block.appendChild(document.createTextNode("Hxp"));
  const strut = document.createElement("span");
  strut.style.cssText =
    "display:inline-block;width:0;height:0;vertical-align:baseline;";
  block.appendChild(strut);

  host.appendChild(inline);
  host.appendChild(block);
  document.body.appendChild(host);

  const content = inline.getBoundingClientRect().height / PROBE_PX;
  const offset =
    strut.getBoundingClientRect().top - block.getBoundingClientRect().top;

  document.body.removeChild(host);

  // offset = (1 - content)/2 + ascent, all in em at this size
  const ascent = offset / PROBE_PX - (1 - content) / 2;
  return { content, ascent };
};

/**
 * Loads each face, measures it, and caches the result.
 *
 * Call before rendering a replica, and await it before a capture — html2canvas
 * reads the DOM as laid out, so metrics that arrive after the capture starts
 * are metrics the PDF never sees.
 *
 * Resolves even if `document.fonts` is unavailable or a face fails to load; the
 * seeded values stand in, which is the same behaviour as before this module
 * existed rather than a regression.
 */
export const loadFormFontMetrics = async (
  faces: readonly FaceSpec[],
): Promise<void> => {
  if (typeof document === "undefined") return;

  const fonts: FontFaceSet | undefined = (document as any).fonts;
  if (fonts?.load) {
    await Promise.all(
      faces.map((f) =>
        fonts.load(cssFont(f, PROBE_PX), "Hxp").catch(() => undefined),
      ),
    );
    // `load` resolving per-face is not quite the same as the set being usable;
    // waiting on `ready` too costs nothing and closes the gap.
    await fonts.ready.catch(() => undefined);
  }

  for (const f of faces) {
    const k = key(f);
    if (measured.has(k)) continue;
    try {
      const m = probe(f);
      // A face that failed to load measures as its fallback. Sanity-bound the
      // result rather than trusting a wild number into every coordinate.
      if (m.content > 0.5 && m.content < 2.5 && m.ascent > 0.3 && m.ascent < 1.5) {
        measured.set(k, m);
      }
    } catch {
      /* seeded value stands */
    }
  }
};
